/* eslint-disable max-lines */
import type { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { DomainErrorCodeMap } from '@core/errors/domain-error-codes';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { ImpersonationService } from '@core/services/impersonation.service';
import { environment } from '@env/environment';
import { HttpStatusMap } from '@kerberos-compliance/compliance-shared-ts/common/enums/http-status.enum';
import type { ResponseUserGrantDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/dtos/response.user-grant.dto';
import type { Observable } from 'rxjs';
import { throwError as observableThrowError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthFacade } from '@/auth/facades/auth.facade';
import { getFeatureForUrl, kerberosConfiguration } from '@/auth/interceptor/kerberos.configuration.interceptor';
import { MissingBearerTokenError } from '@/auth/interceptor/missing-bearer-token.error';
import { KerberosAuthService } from '@/auth/services/kerberos-auth.service';
import type { AuthStateTokenAndOwnGrant, AuthStateTokens, BearerTokenKind } from '@/auth/types/auth.types';
import { BearerTokenKindMap, LogoutReason, UserNotActiveErrorCodes } from '@/auth/types/auth.types';

const dmsAccessDeniedCode = -8705;

/**
 * Resolves which credential a request must carry and reads it from the auth state.
 * The initial sign-in request is authorized with the Hanko session JWT, which the
 * platform exchanges for its own token; every later request uses that platform
 * (Kerberos) token. In debug mode the sign-in exchange is skipped entirely, so
 * the platform token is always used.
 *
 * Returns the token as-is instead of throwing when it is absent: a `throw` from the
 * interceptor body happens during subscription, which turns a missing session into
 * an unhandled error on whichever chain triggered the request (AML-DESK-AW). The
 * caller decides how to fail instead — see the interceptor's main request flow.
 *
 * @param {string} url - The URL to check for the presence of 'sign-in'.
 * @param {AuthStateTokens} authStateTokens - The authentication state tokens object.
 * @returns {{ kind: BearerTokenKind; token: string | null }} The required credential and its current value.
 */
const resolveBearerToken = (
  url: string,
  authStateTokens: AuthStateTokens,
): { kind: BearerTokenKind; token: string | null } => {
  const isExternalSignIn =
    url.includes(environment.config.adpHost.signIn) && !environment.debugFlags.includes('noExternalAuth');

  return isExternalSignIn
    ? { kind: BearerTokenKindMap.Hanko, token: authStateTokens.accessTokenHanko }
    : { kind: BearerTokenKindMap.Platform, token: authStateTokens.accessTokenKerberos };
};

/**
 * Creates authentication headers for HTTP requests.
 *
 * @param {string} kerberosToken - The Kerberos token used for authorization.
 * @param {ResponseUserGrantDto} [grant] - Optional grant information containing tenant and organization IDs.
 * @param {string} [organizationId] - Optional organization ID to be included in the headers.
 * @returns {Record<string, string>} The created authentication headers.
 */
const createAuthHeaders = (
  kerberosToken: string,
  grant?: ResponseUserGrantDto,
  organizationId?: string | null,
): Record<string, string> => {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${kerberosToken}`,
    'tenant-id': grant?.tenantId ?? environment.config.tenant,
  };

  // Add organization ID if available (prioritizing the explicit organizationId parameter)
  const effectiveOrganizationId = organizationId ?? grant?.organizationId;
  if (effectiveOrganizationId) {
    headers['organization-id'] = effectiveOrganizationId;
  }

  return headers;
};

/**
 * Adds authentication headers to an HTTP request.
 *
 * @param {HttpRequest<Request>} request - The HTTP request to which headers will be added.
 * @param {string} kerberosToken - The Kerberos token used for authorization.
 * @param {ResponseUserGrantDto} [grant] - Optional grant information containing tenant and organization IDs.
 * @param {string} [organizationId] - Optional organization ID to be included in the headers.
 * @returns {HttpRequest<Request>} The HTTP request with added authentication headers.
 */
const addAuthHeaders = (
  request: HttpRequest<Request>,
  kerberosToken: string,
  grant?: ResponseUserGrantDto,
  organizationId?: string | null,
): HttpRequest<Request> => {
  const headers = createAuthHeaders(kerberosToken, grant, organizationId);
  return request.clone({
    setHeaders: headers,
  });
};

/**
 * Whether the given error is a recognized "user not active" failure — a
 * disabled, blocked, or expired account (see {@link UserNotActiveErrorCodes}).
 * Accepts `unknown` so it can classify both the interceptor's HttpErrorResponse
 * and the silent token refresh failure, which surfaces when an account is
 * disabled mid-session.
 *
 * @param {unknown} error The error to classify.
 * @returns {boolean} True when the error denotes an inactive user.
 */
const isUserNotActiveError = (error: unknown): boolean => {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  const code = (error.error as { code?: unknown } | undefined)?.code;
  return typeof code === 'number' && (UserNotActiveErrorCodes as readonly number[]).includes(code);
};

/**
 * Extracts a recognized authentication error key from the given
 * HttpErrorResponse. `UserNotActive` covers all the codes the backend may use
 * for a disabled/blocked/expired user (see {@link UserNotActiveErrorCodes}),
 * on both the sign-in and the refresh call.
 *
 * @param {HttpErrorResponse} error The HttpErrorResponse object containing the error details.
 * @returns {'UserNotActive' | 'DmsAccessDenied' | 'AuthTokenInvalid' | undefined} The matching key, or undefined when unrecognized.
 */
const getAuthErrorCode = (
  error: HttpErrorResponse,
): 'UserNotActive' | 'DmsAccessDenied' | 'AuthTokenInvalid' | undefined => {
  if (isUserNotActiveError(error)) {
    return 'UserNotActive';
  }
  const code = (error.error as { code?: unknown } | undefined)?.code;
  if (code === dmsAccessDeniedCode) {
    return 'DmsAccessDenied';
  }
  if (code === DomainErrorCodeMap.AuthTokenInvalid) {
    return 'AuthTokenInvalid';
  }
  return undefined;
};

/**
 * Returns the retry attempt number from the provided HttpRequest.
 *
 * @param {HttpRequest<Request>} request - The HTTP request object.
 * @returns {number} - The retry attempt number.
 */
const getRetryAttempt = (request: HttpRequest<Request>): number => {
  const retryAttempt = request.headers.get(kerberosConfiguration.RETRY_ATTEMPT_HEADER);
  return retryAttempt ? Math.trunc(Number(retryAttempt)) : 0;
};

/**
 * Creates the Kerberos token interceptor as a functional `HttpInterceptorFn`.
 *
 * A factory (rather than a bare `HttpInterceptorFn`) because it holds one piece of
 * long-lived state — the shared, in-flight ADP token refresh — that must persist
 * across requests to de-duplicate concurrent 401 refreshes onto a single refresh.
 * That state lives in this closure. Functional interceptors run within an injection
 * context per request, so the per-request services are resolved with direct
 * `inject()` calls inside the returned interceptor (replacing the class's lazy
 * `Injector.get()` getters). The stateless helpers live at module scope because
 * they depend only on their arguments.
 *
 * @returns {HttpInterceptorFn} The functional Kerberos token interceptor.
 */
export const createKerberosTokenInterceptor = (): HttpInterceptorFn => {
  // The single long-lived piece of state: the shared, in-flight ADP token refresh
  // that de-duplicates concurrent 401 refreshes across requests.
  let refreshInFlight$: Observable<string> | undefined;

  /**
   * Intercepts HTTP requests and adds an authorization token if it is a risk analysis service request.
   *
   * @param {HttpRequest<unknown>} request - The HTTP request to intercept.
   * @param {HttpHandlerFn} next - The next handler function in the chain.
   * @returns {Observable<HttpEvent<unknown>>} An observable that emits the HTTP event after interception.
   */
  return (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const kerberosAuthService = inject(KerberosAuthService);
    const navigationService = inject(NavigationService);
    const impersonationService = inject(ImpersonationService);
    const authFacade = inject(AuthFacade);

    /**
     * Retrieves the Kerberos refresh token from the provided authentication state tokens.
     *
     * @param {AuthStateTokens} authStateTokens - The authentication state tokens object.
     * @returns {string} - The Kerberos refresh token.
     * @throws {Error} - Thrown if the Kerberos token is null or undefined.
     */
    const getKerberosRefreshToken = (authStateTokens: AuthStateTokens): string => {
      const refreshTokenKerberos = authStateTokens.refreshTokenKerberos;

      if (!refreshTokenKerberos) {
        void navigationService.goToContact();
        // IMPORTANT!: this should show an error, on localhost we sometimes have issues with 401, check README file for more information
        throw new Error('Kerberos token is null or undefined');
      }

      return refreshTokenKerberos;
    };

    /**
     * Checks if the application is currently in the logout flow.
     * This prevents token refresh attempts during logout which would cause infinite loops.
     *
     * @returns {boolean} True if on logout flow, false otherwise.
     */
    const isOnLogoutFlow = (): boolean => {
      const currentUrl = navigationService.getCurrentUrl();
      return currentUrl.includes(AppPathsMap.Logout);
    };

    /**
     * Handles a failed silent token refresh. A disabled/blocked/expired account
     * makes the refresh fail with a recognized "user not active" code — route to
     * the dedicated Account Disabled page (matching the in-session -519 handling)
     * instead of the generic session-expired logout, which would otherwise
     * redirect straight to the login screen and hide why the session ended.
     *
     * @param {unknown} refreshError The error thrown by the refresh request.
     * @returns {Observable<never>} An observable that re-throws the refresh error.
     */
    const handleRefreshError = (refreshError: unknown): Observable<never> => {
      if (isUserNotActiveError(refreshError)) {
        void navigationService.goToUserNotActive();
      } else {
        void authFacade.logout(LogoutReason.SessionExpired);
      }
      return observableThrowError(() => refreshError as Error);
    };

    /**
     * Returns the shared, in-flight ADP token refresh, creating it on first use.
     * `shareReplay` collapses concurrent 401s onto a single refresh request and
     * replays its result to every waiter; `finalize` clears the cached request
     * once it settles so the next 401 burst starts a fresh refresh. A failed
     * refresh logs the user out (once) and propagates the error to all waiters.
     *
     * @param {AuthStateTokenAndOwnGrant} authStateTokensAndOwnGrants - The authentication state tokens and grants.
     * @returns {Observable<string>} - Emits the refreshed access token, or errors if the refresh fails.
     */
    const getOrCreateRefresh = (authStateTokensAndOwnGrants: AuthStateTokenAndOwnGrant): Observable<string> => {
      refreshInFlight$ ??= kerberosAuthService
        .refreshToken(getKerberosRefreshToken(authStateTokensAndOwnGrants.authStateTokens))
        .pipe(
          map((response) => {
            if (!response.access_token) {
              throw new Error('Failed to refresh token');
            }
            return response.access_token;
          }),
          catchError(handleRefreshError),
          finalize(() => {
            refreshInFlight$ = undefined;
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      return refreshInFlight$;
    };

    /**
     * Retries the request once a fresh ADP token is available. Concurrent 401s
     * share a single in-flight refresh (see `getOrCreateRefresh`); each then
     * retries its own request with the new token and an incremented attempt header.
     *
     * @param {HttpRequest<Request>} authRequest - The original request to retry.
     * @param {number} retryAttempt - The current retry attempt number.
     * @param {AuthStateTokenAndOwnGrant} authStateTokensAndOwnGrants - The authentication state tokens and grants.
     * @returns {Observable<HttpEvent<Event>>} - An observable that emits the retried request's response.
     */
    const refreshAndRetry = (
      authRequest: HttpRequest<Request>,
      retryAttempt: number,
      authStateTokensAndOwnGrants: AuthStateTokenAndOwnGrant,
    ): Observable<HttpEvent<Event>> => {
      return getOrCreateRefresh(authStateTokensAndOwnGrants).pipe(
        switchMap((newToken) => {
          const retryRequest = addAuthHeaders(
            authRequest.clone({
              setHeaders: {
                [kerberosConfiguration.RETRY_ATTEMPT_HEADER]: String(retryAttempt + 1),
              },
            }),
            newToken,
            authStateTokensAndOwnGrants.ownGrant,
          );
          return next(retryRequest) as Observable<HttpEvent<Event>>;
        }),
      );
    };

    /**
     * Handles a 502 Bad Gateway. On the sign-in endpoint the login component shows
     * an inline error; any other endpoint navigates to the backend-unavailable page,
     * regardless of the domain error code in the response body.
     *
     * @param {HttpErrorResponse} error - The bad gateway error.
     * @param {HttpRequest<Request>} authRequest - The request that was rejected.
     * @returns {Observable<HttpEvent<Event>>} An observable that re-throws (after navigating when not sign-in).
     */
    const handleBadGateway = (
      error: HttpErrorResponse,
      authRequest: HttpRequest<Request>,
    ): Observable<HttpEvent<Event>> => {
      if (!authRequest.url.includes(environment.config.adpHost.signIn)) {
        void navigationService.goToBackendUnavailable();
      }
      return observableThrowError(() => error as Error);
    };

    /**
     * Handles a 403 Forbidden: a rejected sign-in request means the user has no
     * grants; any other rejected request means access is not permitted.
     *
     * @param {HttpErrorResponse} error - The forbidden error.
     * @param {HttpRequest<Request>} authRequest - The request that was rejected.
     * @returns {Observable<HttpEvent<Event>>} An observable that re-throws after navigating.
     */
    const handleForbidden = (
      error: HttpErrorResponse,
      authRequest: HttpRequest<Request>,
    ): Observable<HttpEvent<Event>> => {
      if (authRequest.url.includes(environment.config.adpHost.signIn)) {
        void navigationService.goToNoGrants();
      } else {
        void navigationService.goToUnauthorized();
      }
      return observableThrowError(() => error as Error);
    };

    /**
     * Handles a 401 Unauthorized: routes recognized error codes to their pages,
     * surfaces the sign-in/token-exchange failure, skips refresh where it would
     * loop or doesn't apply, and otherwise refreshes the token and retries.
     *
     * @param {HttpErrorResponse} error - The unauthorized error.
     * @param {HttpRequest<Request>} authRequest - The request that was rejected.
     * @param {AuthStateTokenAndOwnGrant} authStateTokensAndOwnGrants - The current authentication state tokens and grants.
     * @returns {Observable<HttpEvent<Event>>} An observable that retries the request or re-throws the error.
     */
    const handleUnauthorized = (
      error: HttpErrorResponse,
      authRequest: HttpRequest<Request>,
      authStateTokensAndOwnGrants: AuthStateTokenAndOwnGrant,
    ): Observable<HttpEvent<Event>> => {
      const code = getAuthErrorCode(error);
      if (code === 'UserNotActive') {
        void navigationService.goToUserNotActive();
        return observableThrowError(() => error as Error);
      }
      // DmsAccessDenied: the Kerberos session is still valid, only DMS access is
      // denied — don't trigger a refresh, propagate the error to the caller.
      if (code === 'DmsAccessDenied') {
        return observableThrowError(() => error as Error);
      }
      // AuthTokenInvalid: the Hanko JWT is structurally broken — refreshing the
      // Kerberos token won't fix it and must not be attempted.
      // On the sign-in endpoint the login component shows an inline error; any
      // other endpoint navigates to the session-unavailable page.
      if (code === 'AuthTokenInvalid') {
        if (!authRequest.url.includes(environment.config.adpHost.signIn)) {
          void navigationService.goToSessionUnavailable();
        }
        return observableThrowError(() => error as Error);
      }

      // Sign-in / token-exchange 401: the IdP session could not be exchanged for an
      // ADP platform token. No ADP refresh token exists yet, so a refresh is
      // impossible — surface a dedicated screen instead of falling through to the
      // refresh path (which, lacking a refresh token, would misroute to contact).
      // Once the backend exchange succeeds this returns 200 and is never hit.
      if (authRequest.url.includes(environment.config.adpHost.signIn)) {
        void navigationService.goToSessionUnavailable();
        return observableThrowError(() => error as Error);
      }

      // Don't refresh for impersonated sessions or during logout (would loop), nor
      // for the token endpoint itself (prevents infinite refresh loops).
      if (impersonationService.shouldBypassExternalAuth() || isOnLogoutFlow() || authRequest.url.includes('token')) {
        return observableThrowError(() => error as Error);
      }

      const retryAttempt = getRetryAttempt(authRequest);
      if (retryAttempt < kerberosConfiguration.MAX_RETRY_ATTEMPTS) {
        return refreshAndRetry(authRequest, retryAttempt, authStateTokensAndOwnGrants);
      }
      return observableThrowError(() => error as Error);
    };

    /**
     * Handles authentication errors during HTTP requests.
     * Implements a token refresh queue to prevent multiple simultaneous refresh requests.
     * Skips refresh for impersonated users and during logout flow to prevent infinite loops.
     *
     * @param {unknown} error The error that occurred during the HTTP request.
     * @param {HttpRequest<Request>} authRequest The original HTTP request that caused the error.
     * @param {AuthStateTokenAndOwnGrant} authStateTokensAndOwnGrants The current authentication state tokens and grants.
     * @returns {Observable<HttpEvent<Event>>} An observable that emits the HTTP event or an error.
     */
    const handleAuthError = (
      error: unknown,
      authRequest: HttpRequest<Request>,
      authStateTokensAndOwnGrants: AuthStateTokenAndOwnGrant,
    ): Observable<HttpEvent<Event>> => {
      if (!(error instanceof HttpErrorResponse)) {
        return observableThrowError(() => error as Error);
      }

      if (error.status === HttpStatusMap.BadGateway) {
        return handleBadGateway(error, authRequest);
      }

      if (error.status === kerberosConfiguration.FORBIDDEN_STATUS) {
        return handleForbidden(error, authRequest);
      }

      if (error.status === kerberosConfiguration.UNAUTHORIZED_STATUS) {
        return handleUnauthorized(error, authRequest, authStateTokensAndOwnGrants);
      }

      return observableThrowError(() => error as Error);
    };

    // Main request flow (the former `intercept` body). It is declared last because
    // the `const` helper functions above must exist before this code calls them.
    const feature = getFeatureForUrl(request.url); // E.g., 'KYC' or 'RiskAnalysis'

    const organizationId = authFacade.organizationId();
    const authTokensAndOwnGrant = authFacade.selectAuthTokensAndOwnGrant(feature)();
    const { kind, token } = resolveBearerToken(request.url, authTokensAndOwnGrant.authStateTokens);

    if (!token) {
      return observableThrowError(
        () => new MissingBearerTokenError(kind, request.url, navigationService.getCurrentUrl()),
      );
    }

    const authRequest = addAuthHeaders(
      request as HttpRequest<Request>,
      token,
      authTokensAndOwnGrant.ownGrant,
      organizationId,
    );

    return next(authRequest).pipe(catchError((error) => handleAuthError(error, authRequest, authTokensAndOwnGrant)));
  };
};
